import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { useMessages } from '../context/MessagesContext';
import timeAgo from '../utils/timeAgo';
import { Icon } from '../tokens';
import './Messages.css';

const MAX_LEN = 500;

function avatarFor(u) {
  if (!u) return '';
  return u.avatarThumb || u.avatar
    || `https://ui-avatars.com/api/?name=${encodeURIComponent(u.name || 'U')}&background=45E6C2&color=0A0A0A&size=96`;
}

export default function Messages() {
  const { id: routeId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const {
    user, listConversations, getConversation, sendMessage,
    startConversation, markConversationRead,
  } = useAuth();
  const { refreshUnread, setUnreadCount } = useMessages();

  // Destinatario para una conversación nueva (llega por state al venir desde
  // un perfil). Si ya existe hilo con ese usuario, el backend lo reutiliza.
  const pendingRecipient = location.state?.recipient || null;

  const [conversations, setConversations] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [activeId, setActiveId] = useState(routeId || null);

  const [thread, setThread] = useState(null); // { messages, otherUser, hasMore, page }
  const [loadingThread, setLoadingThread] = useState(false);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);

  const scrollRef = useRef(null);
  const composing = !activeId && !!pendingRecipient;

  const scrollToBottom = useCallback((smooth = false) => {
    const el = scrollRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: smooth ? 'smooth' : 'auto' });
  }, []);

  // ── Cargar lista de conversaciones ───────────────────────
  const refreshList = useCallback(async () => {
    try {
      const list = await listConversations();
      setConversations(list);
    } catch { setConversations([]); }
    finally { setLoadingList(false); }
  }, [listConversations]);

  useEffect(() => { refreshList(); }, [refreshList]);

  // Sincroniza el id activo con la URL (/mensajes/:id).
  useEffect(() => { setActiveId(routeId || null); }, [routeId]);

  // ── Abrir un hilo: cargar + marcar leído ─────────────────
  const openThread = useCallback(async (convId) => {
    setLoadingThread(true);
    setThread(null);
    try {
      const data = await getConversation(convId, 1);
      setThread({ messages: data.messages, otherUser: data.otherUser, hasMore: data.hasMore, page: 1 });
      // Marcar como leídos y reflejar en la UI local + badge global.
      await markConversationRead(convId);
      setConversations((prev) => prev.map((c) =>
        c._id === convId ? { ...c, unread: 0 } : c));
      const wasUnread = data.messages.filter((m) => m.senderId !== user?._id && !m.readAt).length;
      if (wasUnread > 0) setUnreadCount((n) => Math.max(0, n - wasUnread));
      refreshUnread();
    } catch {
      setThread({ messages: [], otherUser: null, hasMore: false, page: 1 });
    } finally {
      setLoadingThread(false);
    }
  }, [getConversation, markConversationRead, refreshUnread, setUnreadCount, user]);

  useEffect(() => {
    if (activeId) openThread(activeId);
    else setThread(null);
  }, [activeId, openThread]);

  // Scroll al fondo cuando carga un hilo o llega un mensaje.
  useEffect(() => { if (thread && !loadingThread) scrollToBottom(); }, [thread, loadingThread, scrollToBottom]);

  // ── Cargar página anterior (mensajes más viejos) ─────────
  const loadOlder = async () => {
    if (!thread?.hasMore || !activeId) return;
    const el = scrollRef.current;
    const prevHeight = el?.scrollHeight || 0;
    try {
      const next = thread.page + 1;
      const data = await getConversation(activeId, next);
      setThread((prev) => ({
        ...prev,
        messages: [...data.messages, ...prev.messages],
        hasMore: data.hasMore,
        page: next,
      }));
      // Mantener la posición visual tras anteponer mensajes.
      requestAnimationFrame(() => {
        if (el) el.scrollTop = el.scrollHeight - prevHeight;
      });
    } catch { /* noop */ }
  };

  const selectConversation = (convId) => {
    navigate(`/mensajes/${convId}`);
  };

  const backToList = () => navigate('/mensajes');

  // ── Enviar ───────────────────────────────────────────────
  const handleSend = async () => {
    const text = draft.trim();
    if (!text || text.length > MAX_LEN || sending) return;
    setSending(true);
    try {
      if (composing) {
        const { conversation } = await startConversation(pendingRecipient._id, text);
        setDraft('');
        await refreshList();
        navigate(`/mensajes/${conversation._id}`, { replace: true });
      } else if (activeId) {
        const msg = await sendMessage(activeId, text);
        setDraft('');
        setThread((prev) => prev ? { ...prev, messages: [...prev.messages, msg] } : prev);
        // Subir la conversación al tope de la lista con el nuevo preview.
        setConversations((prev) => {
          const updated = prev.map((c) =>
            c._id === activeId ? { ...c, lastMessage: msg, updatedAt: msg.createdAt } : c);
          return updated.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
        });
        requestAnimationFrame(() => scrollToBottom(true));
      }
    } catch (e) {
      alert(e.response?.data?.message || t('messages.send_error'));
    } finally {
      setSending(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const headerUser = composing ? pendingRecipient : thread?.otherUser;
  const threadOpen = !!activeId || composing;
  const sendDisabled = !draft.trim() || draft.length > MAX_LEN || sending;

  const preview = (c) => {
    const txt = c.lastMessage?.text || '';
    return txt.length > 60 ? `${txt.slice(0, 60)}…` : txt;
  };

  return (
    <div className="messages">
      <Helmet><title>{t('messages.title')} — Troly</title></Helmet>

      <div className={`messages-shell container${threadOpen ? ' messages-shell--thread' : ''}`}>
        {/* ── Panel izquierdo: lista ── */}
        <aside className="msg-list">
          <header className="msg-list__head">
            <h1 className="msg-list__title">{t('messages.title')}</h1>
          </header>

          {loadingList && <p className="msg-empty">{t('messages.loading')}</p>}

          {!loadingList && conversations.length === 0 && !composing && (
            <div className="msg-empty-state">
              <div className="msg-empty-state__icon">{Icon.message(28)}</div>
              <h2 className="msg-empty-state__title">{t('messages.empty_title')}</h2>
              <p className="msg-empty-state__body">{t('messages.empty_body')}</p>
            </div>
          )}

          <ul className="msg-convos">
            {conversations.map((c) => {
              const other = c.otherUser;
              const active = c._id === activeId;
              const unread = c.unread > 0;
              return (
                <li key={c._id}>
                  <button
                    type="button"
                    className={`msg-convo${active ? ' msg-convo--active' : ''}${unread ? ' msg-convo--unread' : ''}`}
                    onClick={() => selectConversation(c._id)}
                  >
                    <img className="msg-convo__av" src={avatarFor(other)} alt={other?.name || ''} />
                    <div className="msg-convo__body">
                      <div className="msg-convo__top">
                        <span className="msg-convo__name">{other?.username || other?.name || '—'}</span>
                        <span className="msg-convo__time">{timeAgo(c.updatedAt, t)}</span>
                      </div>
                      <div className="msg-convo__preview">{preview(c)}</div>
                    </div>
                    {unread && <span className="msg-convo__dot" />}
                  </button>
                </li>
              );
            })}
          </ul>
        </aside>

        {/* ── Panel derecho: hilo ── */}
        <section className="msg-thread">
          {!threadOpen ? (
            <div className="msg-thread__placeholder">
              <div className="msg-empty-state__icon">{Icon.message(28)}</div>
              <p>{t('messages.select_conversation')}</p>
            </div>
          ) : (
            <>
              <header className="msg-thread__head">
                <button type="button" className="msg-thread__back" onClick={backToList} aria-label={t('messages.back')}>
                  <svg width={18} height={18} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 4l-6 6 6 6" />
                  </svg>
                </button>
                {headerUser && (
                  <button
                    type="button"
                    className="msg-thread__user"
                    onClick={() => headerUser?._id && navigate(`/u/${headerUser._id}`)}
                  >
                    <img className="msg-thread__av" src={avatarFor(headerUser)} alt={headerUser.name || ''} />
                    <span className="msg-thread__name">{headerUser.username || headerUser.name}</span>
                  </button>
                )}
              </header>

              <div className="msg-thread__scroll" ref={scrollRef}>
                {loadingThread && <p className="msg-empty">{t('messages.loading')}</p>}

                {!loadingThread && thread?.hasMore && (
                  <button type="button" className="msg-load-more" onClick={loadOlder}>
                    {t('messages.load_more')}
                  </button>
                )}

                {!loadingThread && composing && (
                  <p className="msg-thread__hint">{t('messages.compose_hint', { name: headerUser?.name || '' })}</p>
                )}

                {!loadingThread && !composing && thread?.messages?.length === 0 && (
                  <p className="msg-empty">{t('messages.thread_empty')}</p>
                )}

                {!loadingThread && thread?.messages?.map((m) => {
                  const mine = m.senderId === user?._id;
                  const senderName = mine ? t('messages.you') : (headerUser?.username || headerUser?.name || '');
                  return (
                    <div key={m._id} className={`msg-bubble-row${mine ? ' msg-bubble-row--mine' : ''}`}>
                      <div className={`msg-bubble${mine ? ' msg-bubble--mine' : ''}`}>
                        <span className="msg-bubble__sender">{senderName}</span>
                        <span className="msg-bubble__text">{m.text}</span>
                        <span className="msg-bubble__time">{timeAgo(m.createdAt, t)}</span>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="msg-composer">
                <textarea
                  className="msg-composer__input"
                  value={draft}
                  onChange={(e) => setDraft(e.target.value.slice(0, MAX_LEN + 50))}
                  onKeyDown={onKeyDown}
                  placeholder={t('messages.input_placeholder')}
                  rows={1}
                />
                <div className="msg-composer__meta">
                  <span className={`msg-composer__counter${draft.length > MAX_LEN ? ' msg-composer__counter--over' : ''}`}>
                    {draft.length}/{MAX_LEN}
                  </span>
                  <button
                    type="button"
                    className="msg-composer__send"
                    onClick={handleSend}
                    disabled={sendDisabled}
                  >
                    {sending ? t('messages.sending') : t('messages.send')}
                  </button>
                </div>
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  );
}
